<script>
/**
 * ErrorDescriptionModal
 *
 * A reusable modal wrapper that fetches an error description by its code
 * from the error-description API and displays it using ErrorDescriptionCard.
 *
 * Usage:
 *   <ErrorDescriptionModal
 *     :visible.sync="showError"
 *     error-code="ERR_001"
 *     @action="handleAction"
 *   />
 *
 * Or programmatically via the global mixin/plugin:
 *   this.$showError('ERR_001', { onAction: () => { ... } })
 *
 * Props:
 * - visible:     (Boolean) Controls modal visibility (supports .sync)
 * - errorCode:   (String)  The error code to fetch from the API
 * - overrides:   (Object)  Override any field returned from API (header, subHeader, body, footer, type, buttonText)
 * - backdrop:    (Boolean) Show backdrop overlay (default: true)
 * - persistent:  (Boolean) If true, clicking backdrop won't close the modal
 */
import ErrorDescriptionCard from './ErrorDescriptionCard.vue'
import * as errorDescriptionApi from '@/services/rest-api-sms/lib/error-description/error-description'

export default {
  name: 'ErrorDescriptionModal',
  components: {
    ErrorDescriptionCard,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    errorCode: {
      type: String,
      default: '',
    },
    overrides: {
      type: Object,
      default: () => ({}),
    },
    backdrop: {
      type: Boolean,
      default: true,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      errorData: null,
      fetchError: false,
      isAnimating: false,
    }
  },
  computed: {
    /**
     * Merges API data with any overrides provided via props
     */
    resolvedData() {
      if (!this.errorData && Object.keys(this.overrides).length === 0) {
        return null
      }
      const base = this.errorData || {}
      const customMessage = "{{custom}}"
            
      return {
        code: base.code || this.overrides.code || this.errorCode,
        header: base.header || this.overrides.header || '',
        subHeader: base.sub_header || this.overrides.subHeader || '',
        body: base.body?.includes(customMessage) ? base.body?.replace(customMessage, this.overrides?.body) : (base.body ?? ''), 
        footer: base.footer || this.overrides.footer || '',
        type: base.type || this.overrides.type || 'error',
        buttonText: this.overrides.buttonText || '',
      }
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.onOpen()
      } else {
        this.onClose()
      }
    },
    errorCode(newVal) {
      if (newVal && this.visible) {
        this.fetchErrorDescription(newVal)
      }
    },
  },
  methods: {
    async onOpen() {
      this.isAnimating = true
      document.body.style.overflow = 'hidden'

      if (this.errorCode) {
        await this.fetchErrorDescription(this.errorCode)
      }

      // Trigger enter animation
      this.$nextTick(() => {
        setTimeout(() => {
          this.isAnimating = false
        }, 50)
      })
    },

    onClose() {
      document.body.style.overflow = ''
    },

    async fetchErrorDescription(code) {
      if (!code) return

      this.isLoading = true
      this.fetchError = false

      try {
        // Fetch specific code
        const response = await errorDescriptionApi.getByCode(code)

        if (response?.data) {
          this.errorData = response.data
        } else {
          this.errorData = null
          this.fetchError = true
        }
      } catch (error) {
        console.error(`[ErrorDescriptionModal] Failed to fetch error code: ${code}`, error)
        this.errorData = null
        this.fetchError = true
      } finally {
        this.isLoading = false
      }
    },

    handleAction() {
      this.$emit('action', this.resolvedData)
      this.$emit('update:visible', false)
    },

    handleClose() {
      this.$emit('close')
      this.$emit('update:visible', false)
    },

    handleBackdropClick() {
      if (!this.persistent) {
        this.handleClose()
      }
    },
  },
}
</script>

<template>
  <transition name="edc-modal">
    <div
      v-if="visible"
      class="edc-modal-overlay"
      :class="{ 'edc-modal-overlay--transparent': !backdrop }"
      @click.self="handleBackdropClick"
    >
      <div class="edc-modal-container" :class="{ 'edc-modal-container--entering': isAnimating }">
        <!-- Loading State -->
        <div v-if="isLoading" class="edc-modal-loading">
          <div class="edc-spinner">
            <div class="edc-spinner__ring"></div>
          </div>
          <p class="edc-modal-loading__text">Memuat informasi...</p>
        </div>

        <!-- Error Fetch State (fallback) -->
        <ErrorDescriptionCard
          v-else-if="fetchError && !resolvedData"
          :header="'Terjadi Kesalahan'"
          :sub-header="'Tidak dapat memuat detail error. Silakan coba lagi.'"
          :body="
            '<p>Error code: <strong>' + errorCode + '</strong> tidak ditemukan di database.</p>'
          "
          type="error"
          button-text="Tutup"
          :closable="true"
          @action="handleClose"
          @close="handleClose"
        />

        <!-- Resolved Error Data -->
        <ErrorDescriptionCard
          v-else-if="resolvedData"
          :code="resolvedData.code"
          :header="resolvedData.header"
          :sub-header="resolvedData.subHeader"
          :body="resolvedData.body"
          :footer="resolvedData.footer"
          :type="resolvedData.type"
          :button-text="resolvedData.buttonText"
          :closable="true"
          @action="handleAction"
          @close="handleClose"
        />
      </div>
    </div>
  </transition>
</template>

<style scoped>
/* ============================================ */
/* MODAL OVERLAY                                 */
/* ============================================ */
.edc-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
}
.edc-modal-overlay--transparent {
  background: transparent;
  backdrop-filter: none;
}

/* ============================================ */
/* MODAL CONTAINER                               */
/* ============================================ */
.edc-modal-container {
  width: 100%;
  max-width: min(90vw, 720px);
  animation: edc-slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.edc-modal-container--entering {
  opacity: 0;
  transform: translateY(20px) scale(0.96);
}

/* ============================================ */
/* LOADING STATE                                 */
/* ============================================ */
.edc-modal-loading {
  background: #ffffff;
  border-radius: 16px;
  padding: 48px 32px;
  text-align: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}
.edc-modal-loading__text {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.9rem;
  color: #6b7280;
  margin-top: 16px;
}

/* Spinner */
.edc-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto;
}
.edc-spinner__ring {
  width: 100%;
  height: 100%;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: edc-spin 0.8s linear infinite;
}

/* ============================================ */
/* ANIMATIONS                                    */
/* ============================================ */
@keyframes edc-slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes edc-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transition classes for v-if */
.edc-modal-enter-active {
  transition: opacity 0.3s ease;
}
.edc-modal-leave-active {
  transition: opacity 0.2s ease;
}
.edc-modal-enter,
.edc-modal-leave-to {
  opacity: 0;
}
.edc-modal-enter .edc-modal-container,
.edc-modal-leave-to .edc-modal-container {
  transform: translateY(20px) scale(0.96);
}

@media (max-width: 640px) {
  .edc-modal-container {
    max-width: 92vw;
  }
}
</style>
